// modules/facturacion/components/PrefacturaComparativaDialog.jsx
import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Divider, Box, Paper, Typography,
  Table, TableHead, TableBody, TableRow, TableContainer, Checkbox, Button, CircularProgress, Alert, Chip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import axios from "../../../config/axiosConfig";
import {
  Th, Td, money, parseDecimal, stripCuotaDetalle, cuotaActiva, softPanelSx,
  HistorialFacturasPanel, buildInternalGroups, marcarLineaPrincipal,
  mesActual, mesPrevio, yearActual, yearPrevio, getLegalIdValue,
} from "./PrefacturaServicioModal";
import { imprimirPrefacturasCombinado } from "../services/facturacionService";

// Vista comparativa masiva: para cada servicio seleccionado en Facturación Masiva, arma
// automáticamente el mismo borrador (Conceptos Internos + Líneas Fiscales) que ya calcula
// el modal individual (mismo endpoint /api/facturas/servicio-detalle), lado a lado con la
// última factura, para revisar y confirmar en bloque. A diferencia del modal individual,
// acá NO se edita línea por línea — para ajustar un caso puntual se sigue usando el botón
// "Pre-facturar" individual de esa fila.
const cargarDatosServicio = async (servicio) => {
  const [resDetalle, resHistorial] = await Promise.all([
    axios.get("/api/facturas/servicio-detalle", { params: { cod_servicio_cliente: servicio.cod_servicio_cliente } }),
    axios.get(`/api/facturas/reporte/${servicio.cod_servicio_cliente}`, { params: { excluir_anuladas: 1 } }),
  ]);
  const rawDetalle = resDetalle.data;
  const data = rawDetalle?.data ?? rawDetalle;
  const rawHistorial = resHistorial.data?.data ?? resHistorial.data;

  const esAdelantado = servicio?.pago === "adelantado";
  const mesDefaultIdx = esAdelantado ? mesActual : mesPrevio;
  const yearDefault = esAdelantado ? yearActual : yearPrevio;

  const lineasCrudas = Array.isArray(data?.detalles) ? data.detalles : [];
  const lineas = lineasCrudas.map((d) => ({
    ...d,
    descripcion_interna: d.descripcion_interna || stripCuotaDetalle(d.detalle),
    descuento_interno: d.descuento_interno || 0,
    mes_servicio: d.mes_servicio || String(mesDefaultIdx + 1),
    year: String(d.year || yearDefault),
    con_cuota: d.mostrar_cuota !== "NO",
    mostrar_cuota: d.mostrar_cuota || "SI",
  }));

  return {
    servicio,
    data,
    historial: {
      facturas: Array.isArray(rawHistorial?.facturas) ? rawHistorial.facturas.slice(0, 3) : [],
      ultimaFacturaDetalle: rawHistorial?.ultimaFacturaDetalle || null,
    },
    detalles: marcarLineaPrincipal(lineas),
    mesDefaultIdx,
    yearDefault,
  };
};

const buildRequestBody = (item) => {
  const { servicio, data, historial, detalles, mesDefaultIdx, yearDefault } = item;
  const detallesFiscales = detalles.filter((d) => !d.concepto_pendiente);
  // Si el servicio no tiene NIT/razón social propios (ni a nivel servicio ni cliente),
  // se cae a los datos con los que se facturó la última vez, para no dejarlo vacío.
  const ultimaFactura = historial?.ultimaFacturaDetalle;

  return {
    id_servicio_cliente: servicio.cod_servicio_cliente,
    cod_cliente: servicio.cod_cliente,
    id_legal: getLegalIdValue(data?.id_legal ? `servicio-${data.id_legal}` : "") || null,
    nit: data?.nit || ultimaFactura?.nit || "",
    fac_Nombre: data?.nombre_facturar || ultimaFactura?.nombre || "",
    tipo_documento_id: Number(data?.tipo_documento_identidad || 5),
    fac_ind: "no",
    mes: String(mesDefaultIdx + 1),
    year: yearDefault,
    sucursal: data?.sucursal || servicio.sucursal_facturar || "La Paz",
    codigoMoneda: Number(data?.moneda || servicio.moneda || 2),
    venta: "Alquiler",
    tipo: "TRS",
    detalles: detallesFiscales.map((d) => ({
      id_fac_item: d.id_fac_item || null,
      id_regla_detalle: d.id_regla_detalle || null,
      id_detalle_grupo: d.id_detalle_grupo || null,
      detalle: stripCuotaDetalle(d.detalle),
      descripcion_interna: d.descripcion_interna || null,
      aplicar_adicional: d.aplicar_adicional !== false,
      cantidad: parseDecimal(d.cantidad),
      precio_unitario: parseDecimal(d.precio_unitario),
      precio_unitario_base: d.precio_unitario_base !== undefined ? parseDecimal(d.precio_unitario_base) : undefined,
      descuento_interno: parseDecimal(d.descuento_interno),
      tipo: String(d.tipo || ""),
      mes_servicio: d.mes_servicio,
      year: d.year || String(yearDefault),
      mostrar_cuota: cuotaActiva(d) ? "SI" : "NO",
      metodo_calculo: d.metodo_calculo || null,
      group_key: d.group_key || null,
      p_3: "",
    })),
  };
};

const ItemComparativo = ({ item, checked, onToggle, resultado }) => {
  if (item.loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 1.5, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={22} />
      </Paper>
    );
  }
  if (item.error) {
    return (
      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          #{item.servicio.cod_servicio_cliente} · {item.servicio.razon_social}
        </Typography>
        <Alert severity="error" sx={{ py: 0.25 }}>{item.error}</Alert>
      </Paper>
    );
  }

  const detallesFiscales = item.detalles.filter((d) => !d.concepto_pendiente);
  const conceptosInternos = buildInternalGroups(item.detalles);
  const sinDatos = detallesFiscales.length === 0;

  const ultimaFactura = item.historial?.ultimaFacturaDetalle;
  const usaDatosDeUltimaFactura = !item.data?.nit && !!ultimaFactura?.nit;
  const nitResuelto = item.data?.nit || ultimaFactura?.nit || "";
  const nombreResuelto = item.data?.nombre_facturar || ultimaFactura?.nombre || "";
  const sinNit = !nitResuelto;

  return (
    <Paper variant="outlined" sx={{ mb: 1.5, overflow: "hidden" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.75, bgcolor: "action.hover" }}>
        <Checkbox size="small" checked={checked} onChange={onToggle} disabled={sinDatos || sinNit || !!resultado} sx={{ p: 0.5 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
          #{item.servicio.cod_servicio_cliente} · {item.servicio.razon_social}
        </Typography>
        <Chip size="small" label={item.servicio.servicio_nombre} sx={{ height: 18, fontSize: "0.62rem" }} />
        {!sinDatos && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.68rem" }}>
            NIT {nitResuelto || "—"} · {nombreResuelto || "sin razón social registrada"}
          </Typography>
        )}
        {usaDatosDeUltimaFactura && (
          <Chip size="small" color="info" variant="outlined" label="NIT tomado de la última factura" sx={{ height: 18, fontSize: "0.62rem" }} />
        )}
        {sinNit && (
          <Chip size="small" color="warning" label="Sin NIT — usar Pre-facturar individual" sx={{ height: 18, fontSize: "0.62rem" }} />
        )}
        {sinDatos && (
          <Chip size="small" color="warning" label="Sin reglas de facturación — usar Pre-facturar individual" sx={{ height: 18, fontSize: "0.62rem" }} />
        )}
        {resultado?.ok && (
          <Chip size="small" color="success" icon={<CheckCircleIcon sx={{ fontSize: 14 }} />} label={`Prefactura #${resultado.cod} generada`} sx={{ height: 20, fontSize: "0.66rem", ml: "auto" }} />
        )}
        {resultado?.error && (
          <Chip size="small" color="error" icon={<ErrorOutlineIcon sx={{ fontSize: 14 }} />} label={resultado.error} sx={{ height: 20, fontSize: "0.66rem", ml: "auto" }} />
        )}
      </Box>
      <Divider />
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
        <Box sx={{ p: 1, minWidth: 0, borderRight: "1px solid", borderColor: "divider" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5, gap: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 800, color: "#b45309", textTransform: "uppercase", whiteSpace: "nowrap" }}>
              Factura Anterior
            </Typography>
            {/* factura.mes es texto libre — puede ser "3", "Enero", o un rango tipo "1-2-3-4-5-6"
                (mismo campo "Periodo" que se ve en el detalle de factura) — se muestra tal cual,
                sin convertir ni exigir que year sea un número válido. */}
            {ultimaFactura?.factura && (
              <Chip
                size="small"
                variant="outlined"
                label={
                  <>
                    <Box component="span" sx={{ color: "error.main", fontWeight: 800, fontSize: "0.78rem" }}>
                      {ultimaFactura.factura.mes || "-"}
                    </Box>
                    {` / ${ultimaFactura.factura.year || "-"}`}
                  </>
                }
                sx={{ height: 18, fontSize: "0.62rem" }}
              />
            )}
          </Box>
          <Box sx={{ overflowX: "auto" }}>
            <HistorialFacturasPanel historial={item.historial} compact />
          </Box>
        </Box>
        <Box sx={{ p: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5, gap: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 800, color: "#008080", textTransform: "uppercase", whiteSpace: "nowrap" }}>
              Pre-Factura por Emitir
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              color="primary"
              label={
                <>
                  <Box component="span" sx={{ color: "error.main", fontWeight: 800, fontSize: "0.78rem" }}>
                    {item.mesDefaultIdx + 1}
                  </Box>
                  {` / ${item.yearDefault}`}
                </>
              }
              sx={{ height: 18, fontSize: "0.62rem" }}
            />
          </Box>

          {conceptosInternos.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: "block", mb: 0.25 }}>
                Conceptos internos ({conceptosInternos.length})
              </Typography>
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ "& td": { py: 0.25, px: 0.6, fontSize: "0.72rem", whiteSpace: "nowrap" }, ...softPanelSx }}>
                  <TableHead>
                    <TableRow>
                      <Th>Cant.</Th>
                      <Th sx={{ whiteSpace: "normal" }}>Descripción interna</Th>
                      <Th>Periodo</Th>
                      <Th align="right">P. Base</Th>
                      <Th align="right">Total</Th>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {conceptosInternos.map((g) => (
                      <TableRow key={g.group_key}>
                        <Td>{g.cantidad}</Td>
                        <Td sx={{ whiteSpace: "normal", minWidth: 180 }}>{g.descripcion_interna}</Td>
                        <Td>{g.con_cuota ? `${g.mes_servicio} ${g.year}` : "-"}</Td>
                        <Td align="right">{money(g.precio_unitario_base)}</Td>
                        <Td align="right">{money((parseDecimal(g.cantidad) * parseDecimal(g.precio_unitario_base)) - parseDecimal(g.descuento_interno))}</Td>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: "block", mb: 0.25 }}>
            Líneas fiscales para Hippo ({detallesFiscales.length})
          </Typography>
          {sinDatos ? (
            <Typography variant="caption" color="text.secondary">Sin líneas generadas automáticamente para este servicio.</Typography>
          ) : (
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ "& td": { py: 0.25, px: 0.6, fontSize: "0.72rem", whiteSpace: "nowrap" } }}>
                <TableHead>
                  <TableRow>
                    <Th>Cant.</Th>
                    <Th sx={{ whiteSpace: "normal" }}>Detalle</Th>
                    <Th>Periodo</Th>
                    <Th align="right">P. Unit.</Th>
                    <Th align="right">Subtotal</Th>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detallesFiscales.map((d, idx) => (
                    <TableRow key={`${d.group_key || "linea"}-${idx}`}>
                      <Td>{d.cantidad}</Td>
                      <Td sx={{ whiteSpace: "normal", minWidth: 180 }}>{stripCuotaDetalle(d.detalle)}</Td>
                      <Td>{cuotaActiva(d) ? `${d.mes_servicio} ${d.year}` : "-"}</Td>
                      <Td align="right">{money(d.precio_unitario)}</Td>
                      <Td align="right">{money((parseDecimal(d.cantidad) * parseDecimal(d.precio_unitario)) - parseDecimal(d.descuento_interno))}</Td>
                    </TableRow>
                  ))}
                  <TableRow>
                    <Td colSpan={4} align="right"><strong>Total Bs.</strong></Td>
                    <Td align="right">
                      <strong>
                        {money(detallesFiscales.reduce((acc, d) => acc + (parseDecimal(d.cantidad) * parseDecimal(d.precio_unitario) - parseDecimal(d.descuento_interno)), 0))}
                      </strong>
                    </Td>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

const PrefacturaComparativaDialog = ({ open, onClose, servicios = [], onSuccess }) => {
  const [items, setItems] = useState([]);
  const [seleccionados, setSeleccionados] = useState({});
  const [resultados, setResultados] = useState({});
  const [generando, setGenerando] = useState(false);

  useEffect(() => {
    if (!open || !servicios.length) return;
    setResultados({});
    setItems(servicios.map((servicio) => ({ servicio, loading: true })));
    setSeleccionados(Object.fromEntries(servicios.map((s) => [s.cod_servicio_cliente, true])));

    servicios.forEach(async (servicio) => {
      try {
        const item = await cargarDatosServicio(servicio);
        const sinDatos = item.detalles.filter((d) => !d.concepto_pendiente).length === 0;
        const sinNit = !(item.data?.nit || item.historial?.ultimaFacturaDetalle?.nit);
        if (sinDatos || sinNit) {
          setSeleccionados((prev) => ({ ...prev, [servicio.cod_servicio_cliente]: false }));
        }
        setItems((prev) => prev.map((it) => (it.servicio.cod_servicio_cliente === servicio.cod_servicio_cliente ? { ...item, loading: false } : it)));
      } catch (e) {
        setSeleccionados((prev) => ({ ...prev, [servicio.cod_servicio_cliente]: false }));
        setItems((prev) => prev.map((it) => (it.servicio.cod_servicio_cliente === servicio.cod_servicio_cliente
          ? { servicio, loading: false, error: e?.response?.data?.message || "No se pudo cargar el borrador de este servicio." }
          : it)));
      }
    });
  }, [open, servicios]);

  const totalSeleccionados = Object.values(seleccionados).filter(Boolean).length;

  const handleGenerar = async () => {
    setGenerando(true);
    const pendientes = items.filter((it) => !it.error && !it.loading && seleccionados[it.servicio.cod_servicio_cliente] && !resultados[it.servicio.cod_servicio_cliente]);

    // Se abre la pestaña ya (en blanco) antes de cualquier await, en el mismo gesto de
    // click del usuario — si se abre después, el navegador la bloquea como popup.
    const pdfWindow = window.open("", "_blank");
    if (pdfWindow) {
      pdfWindow.opener = null;
      pdfWindow.document.title = "Generando pre-facturas...";
      pdfWindow.document.body.innerHTML =
        '<div style="font-family:Arial,sans-serif;padding:24px;color:#334155">Generando pre-facturas y su PDF combinado...</div>';
    }

    const codsGenerados = [];
    for (const item of pendientes) {
      try {
        const body = buildRequestBody(item);
        const res = await axios.post("/api/facturas", body);
        const cod = (res.data?.data ?? res.data)?.cod;
        setResultados((prev) => ({ ...prev, [item.servicio.cod_servicio_cliente]: { ok: true, cod } }));
        if (cod) codsGenerados.push(cod);
      } catch (e) {
        setResultados((prev) => ({
          ...prev,
          [item.servicio.cod_servicio_cliente]: { ok: false, error: e?.response?.data?.message || "Error al generar" },
        }));
      }
    }

    if (pdfWindow) {
      if (codsGenerados.length) {
        try {
          const { data } = await imprimirPrefacturasCombinado(codsGenerados);
          const pdfUrl = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
          pdfWindow.location.replace(pdfUrl);
          setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
        } catch {
          pdfWindow.document.body.innerHTML =
            '<div style="font-family:Arial,sans-serif;padding:24px;color:#b91c1c">Las pre-facturas se generaron, pero no se pudo armar el PDF combinado. Imprimilas individualmente desde la pestaña "Pre-Facturas".</div>';
        }
      } else {
        pdfWindow.close();
      }
    }

    setGenerando(false);
    onSuccess?.();
  };

  const hayGeneradas = Object.values(resultados).some((r) => r.ok);

  return (
    <Dialog open={open} onClose={generando ? undefined : onClose} maxWidth="xl" fullWidth
      PaperProps={{ sx: { m: 0.5, maxHeight: "calc(100% - 8px)" } }}>
      <DialogTitle sx={{ fontSize: "0.95rem", fontWeight: 800 }}>
        Pre-facturar (comparativo) — {servicios.length} servicio(s)
      </DialogTitle>
      <Divider />
      <DialogContent sx={softPanelSx}>
        {items.map((item) => (
          <ItemComparativo
            key={item.servicio.cod_servicio_cliente}
            item={item}
            checked={!!seleccionados[item.servicio.cod_servicio_cliente]}
            resultado={resultados[item.servicio.cod_servicio_cliente]}
            onToggle={() => setSeleccionados((prev) => ({
              ...prev,
              [item.servicio.cod_servicio_cliente]: !prev[item.servicio.cod_servicio_cliente],
            }))}
          />
        ))}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 2, py: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: "auto" }}>
          {totalSeleccionados} seleccionado(s)
        </Typography>
        <Button onClick={onClose} disabled={generando}>{hayGeneradas ? "Cerrar" : "Cancelar"}</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleGenerar}
          disabled={generando || totalSeleccionados === 0}
          startIcon={generando ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {generando ? "Generando..." : "Generar Pre-facturas Seleccionadas"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrefacturaComparativaDialog;
